import { useState } from 'react'
import { useAuth } from '../lib/AuthContext'

function friendlyError(err) {
  const code = err?.code || ''
  if (code.includes('email-already-in-use')) return 'That email already has an account - sign in instead.'
  if (code.includes('weak-password')) return 'Password should be at least 6 characters.'
  if (code.includes('invalid-email')) return 'That email address looks invalid.'
  if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
    return 'Incorrect email or password.'
  }
  if (code.includes('too-many-requests')) return 'Too many attempts - wait a bit and try again.'
  return 'Something went wrong. Please try again.'
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  )
}

function GoogleButton({ busy, onClick, label = 'Continue with Google' }) {
  return (
    <button type="button" className="btn btn-google full-width" onClick={onClick} disabled={busy}>
      <GoogleIcon />
      {label}
    </button>
  )
}

function BrandPanel() {
  return (
    <div className="login-brand-panel">
      <img src="/health-icons/icon-512.png" alt="" className="login-brand-mark" />
      <h2 className="login-brand-title">HealthNest</h2>
      <p className="login-brand-tagline">Your 90-day plan, tracked in full - workouts, meals, macros, and progress, all in one place.</p>
      <ul className="login-brand-features">
        <li>Full macro + micronutrient tracking</li>
        <li>Workout logging with personal records</li>
        <li>Pantry &amp; shopping list management</li>
        <li>Progress photos and weekly reviews</li>
      </ul>
    </div>
  )
}

export default function Login() {
  const { login, signup, resetPassword, loginWithGoogle } = useAuth()
  const [mode, setMode] = useState('signin') // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const switchMode = (next) => {
    setMode(next)
    setError('')
    setResetSent(false)
  }

  const submitSignIn = async (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await login(email, password)
    } catch (err) {
      setError(friendlyError(err))
    } finally {
      setBusy(false)
    }
  }

  const submitSignUp = async (e) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setBusy(true)
    try {
      await signup(email, password)
      // Signed in automatically by Firebase - AppShell picks up the new
      // session and, since there's no profile yet, routes straight into the
      // Onboarding wizard to collect name/stats/preferences one step at a
      // time (see Health.jsx). The plan start date is chosen separately on
      // first load of Today (StartDatePrompt).
    } catch (err) {
      setError(friendlyError(err))
      setBusy(false)
    }
  }

  const submitGoogle = async () => {
    setError('')
    setBusy(true)
    try {
      await loginWithGoogle()
      // Works for both sign-in and sign-up: Firebase creates the account on
      // first use automatically. A brand-new account has no profile doc yet,
      // so AppShell routes it into Onboarding the same as an email signup.
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        setBusy(false)
        return
      }
      setError(friendlyError(err))
      setBusy(false)
    }
  }

  const submitReset = async (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await resetPassword(email)
      setResetSent(true)
    } catch (err) {
      setError(friendlyError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <BrandPanel />
      <div className="login-form-side">
        <div className="login-card">
          <h1>HealthNest</h1>
          <p className="muted">
            {mode === 'signin' && 'Sign in to continue'}
            {mode === 'signup' && 'Create your account'}
            {mode === 'reset' && 'Reset your password'}
          </p>

          {mode === 'signin' && (
            <form onSubmit={submitSignIn}>
              <label>
                Email
                <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </label>
              <label>
                Password
                <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
              {error && <p className="error-text">{error}</p>}
              <button className="btn btn-primary full-width" type="submit" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="login-divider"><span>or</span></div>
              <GoogleButton busy={busy} onClick={submitGoogle} label="Sign in with Google" />
              <div className="login-links">
                <button type="button" className="login-link" onClick={() => switchMode('reset')}>Forgot password?</button>
                <button type="button" className="login-link" onClick={() => switchMode('signup')}>Create an account</button>
              </div>
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={submitSignUp}>
              <label>
                Email
                <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </label>
              <label>
                Password
                <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
              <label>
                Confirm password
                <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </label>

              {error && <p className="error-text">{error}</p>}
              <button className="btn btn-primary full-width" type="submit" disabled={busy}>
                {busy ? 'Creating account…' : 'Create account'}
              </button>
              <div className="login-divider"><span>or</span></div>
              <GoogleButton busy={busy} onClick={submitGoogle} label="Sign up with Google" />
              <div className="login-links">
                <button type="button" className="login-link" onClick={() => switchMode('signin')}>Already have an account? Sign in</button>
              </div>
            </form>
          )}

          {mode === 'reset' && (
            <form onSubmit={submitReset}>
              <label>
                Email
                <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </label>
              {error && <p className="error-text">{error}</p>}
              {resetSent && <p className="success-text">Reset link sent - check your inbox.</p>}
              <button className="btn btn-primary full-width" type="submit" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <div className="login-links">
                <button type="button" className="login-link" onClick={() => switchMode('signin')}>Back to sign in</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
